'use client';

import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import { useI18n } from '@/lib/i18n';
import { SheepStrip, ThinSheepStrip } from '@/components/redesign/sheep-strip';
import {
  IlluProject,
  IlluFinished,
  IlluYarn,
  IlluAccessories,
  IlluTechniques,
  IlluInspiration,
} from '@/components/redesign/illustrations';
import OnboardingTour from '@/components/OnboardingTour';

// B1 "Newsprint Zine" redesign — see Knitter's Library Design System
// handoff bundle (`dir-b1-newsprint.jsx`). Cream newsprint paper, true
// black ink, halftone dot patterns, electric blue spot color, condensed
// display + italic serif.
const fontSerif = "'Fraunces', 'Times New Roman', serif";

const HALFTONE = {
  backgroundImage:
    'radial-gradient(circle, rgba(15,14,12,0.55) 1px, transparent 1.4px)',
  backgroundSize: '5px 5px',
};

const HALFTONE_LIGHT = {
  backgroundImage:
    'radial-gradient(circle, rgba(15,14,12,0.25) 1px, transparent 1.4px)',
  backgroundSize: '4px 4px',
};

type SectionDef = {
  numeral: string;
  name: string;
  href: string;
  Icon: React.ComponentType<{ size?: number; w?: number }>;
  half: boolean;
  /** Per-section accent color flashed during the press/tap state. */
  accent: string;
  count: number | null;
};

// Module-level "install reminder already shown this page-load" flag. Lives
// outside the component so it survives client-side navigation (returning to
// Home won't re-pop the reminder) but resets on a real page load / reopen /
// reload — so the reminder appears every time the app is freshly opened in a
// browser (Veronika 2026-06-18). Reset only by a new JS context.
let installNudgeShownThisLoad = false;

export default function HomePage() {
  const { t } = useI18n();

  // Profile name comes from `/profile` (localStorage `knitter-name`).
  // Used in the masthead's tagline line. Falls back to a quiet
  // placeholder if no name has been set yet. Build 1144 also reads
  // `knitter-self-message` — when non-empty, it overrides the
  // default tagline entirely (replaces "{NAME}, GRAB YOUR NEEDLES
  // — YOUR YARNS ARE BUZZING" with whatever the user typed in
  // profile/you). Veronika 2026-05-08 ask.
  const [profileName, setProfileName] = useState('');
  const [selfMessage, setSelfMessage] = useState('');
  useEffect(() => {
    const onUpdate = () => {
      setProfileName(localStorage.getItem('knitter-name')?.trim() ?? '');
      setSelfMessage(localStorage.getItem('knitter-self-message')?.trim() ?? '');
    };
    onUpdate();
    window.addEventListener('storage', onUpdate);
    return () => window.removeEventListener('storage', onUpdate);
  }, []);

  // 2026-05-08 build 1141 — Veronika: "everytime I go back from
  // anywhere of the app to Home page, I want the last visited card
  // to appear the full color with white text". When the user taps
  // a section card we stash its href in sessionStorage; on the
  // home page we read that back and apply the active-state class
  // to the matching card so it reads as "you were just here".
  // sessionStorage clears when the tab/PWA is closed, so a fresh
  // app launch never starts with a highlighted card — the cue is
  // navigational, not persistent.
  //
  // 2026-06-11 Veronika: on a *cold* app launch (PWA fully closed,
  // sessionStorage empty) show ALL six cards in their accent color
  // as a welcome flourish. The moment she opens a section and
  // returns, `knitlib:lastCardHref` exists, so we fall back to the
  // build-1141 behavior (only the last-visited card stays colored).
  // The absence of that key is exactly the "fresh launch" signal.
  const [lastVisitedHref, setLastVisitedHref] = useState<string | null>(null);
  const [freshLaunch, setFreshLaunch] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = sessionStorage.getItem('knitlib:lastCardHref');
    if (stored) setLastVisitedHref(stored);
    else setFreshLaunch(true);
  }, []);
  // Live counts pulled straight from Dexie. Each section card shows the
  // current size of its underlying table, formatted as a 2-digit string.
  // 2026-05-09 build 1185 — every count filters out soft-deleted rows
  // (deletedAt set) so deletions immediately reflect on the home cards.
  // 2026-05-10 build 1258 — Veronika: the "Projects" home card
  // count should equal what you'll find on /active, which lists
  // three buckets (on needles + hibernating + future). Previously
  // the card only counted in-progress, so the card said "2" but
  // /active showed 4 across the three categories. Sum all three.
  const currentProjectsCount = useLiveQuery(
    () => db.projects.where('status').anyOf(['in-progress', 'hibernating', 'future']).filter(p => p.deletedAt == null).count(),
    [],
    null,
  );
  const finishedCount = useLiveQuery(
    () => db.projects.where('status').equals('finished').filter(p => p.deletedAt == null).count(),
    [],
    null,
  );
  const yarnsCount = useLiveQuery(
    () => db.yarns.filter(y => y.deletedAt == null).count(),
    [],
    null,
  );
  const inventoryCount = useLiveQuery(
    () => db.inventory.filter(i => i.deletedAt == null).count(),
    [],
    null,
  );
  const techniquesCount = useLiveQuery(
    () => db.techniques.filter(t => t.deletedAt == null).count(),
    [],
    null,
  );
  const inspirationsCount = useLiveQuery(
    () => db.inspirations.filter(i => i.deletedAt == null).count(),
    [],
    null,
  );

  // First-run onboarding tour (Sebastian 2026-06-08). Shown ONLY to a
  // brand-new user — an empty library that hasn't seen the tour before.
  // Anyone with their own entries never sees it. A Settings "replay" can
  // force it regardless, via a one-shot sessionStorage flag. The tour's
  // sample content lives entirely in the component (never the DB), so
  // dismissing it leaves the app clean — nothing to sync or undo.
  const TOUR_SEEN_KEY = 'knitlib-onboarding-seen-v1';
  const tourCounts = [currentProjectsCount, finishedCount, yarnsCount, inventoryCount, techniquesCount, inspirationsCount];
  const tourCountsLoaded = tourCounts.every(c => c !== null && c !== undefined);
  const isEmptyLibrary = tourCountsLoaded && tourCounts.every(c => c === 0);
  // Default to "seen" so returning users never get a flash before localStorage
  // is read; the effect flips it to false only when the flag is absent.
  const [tourSeen, setTourSeen] = useState(true);
  const [forceTour, setForceTour] = useState(false);
  useEffect(() => {
    setTourSeen(localStorage.getItem(TOUR_SEEN_KEY) === '1');
    // ?tour=browser|app force-shows that version of the tour — a testing
    // affordance (Veronika 2026-06-13) because the dev site opens as an
    // installed app on her phone, so she can't otherwise preview the BROWSER
    // tour. The chosen version is stored in sessionStorage (read by
    // OnboardingTour) so it survives the Settings → Replay navigation, which
    // drops query params. ?tour=auto clears the override.
    const forcedTour = new URLSearchParams(window.location.search).get('tour');
    if (forcedTour === 'browser' || forcedTour === 'app') {
      sessionStorage.setItem('knitlib-tour-ctx', forcedTour);
      setForceTour(true);
    } else if (forcedTour === 'auto') {
      sessionStorage.removeItem('knitlib-tour-ctx');
    }
    if (sessionStorage.getItem('knitlib-replay-tour') === '1') {
      sessionStorage.removeItem('knitlib-replay-tour');
      setForceTour(true);
    }
  }, []);
  const showTour = forceTour || (isEmptyLibrary && !tourSeen);
  const dismissTour = () => {
    localStorage.setItem(TOUR_SEEN_KEY, '1');
    setTourSeen(true);
    setForceTour(false);
  };

  // Option B (Veronika 2026-06-18): browser users get the INSTALL reminder
  // (only the install slide) EVERY time they open the app in a browser, until
  // they're running the installed PWA. Standalone (installed) users never see
  // it, and a brand-new user's full welcome tour (which already covers install)
  // takes precedence. Gating uses a MODULE-level "shown this page-load" flag,
  // NOT sessionStorage: it must re-appear on every fresh open / reload (a new
  // JS context resets the flag — Veronika 2026-06-18: "it should always be
  // shown when I open it from browser"), but NOT on client-side navigation
  // within the app (returning to Home shouldn't re-pop the full-screen slide).
  // Gated on `tourCountsLoaded` so a new/empty user gets the full tour, not a
  // flash of the nudge first.
  const [installNudge, setInstallNudge] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !tourCountsLoaded) return;
    if (installNudgeShownThisLoad) return;
    const standalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    const newUserTour = isEmptyLibrary && !tourSeen; // the full welcome tour will show
    if (!standalone && !newUserTour && !forceTour) {
      setInstallNudge(true);
      installNudgeShownThisLoad = true;
    }
  }, [tourCountsLoaded, isEmptyLibrary, tourSeen, forceTour]);
  const dismissInstallNudge = () => setInstallNudge(false);

  // Each card carries its own accent color. The color flashes only on
  // press/tap (CSS `:active`) — no static fill, so the grid stays calm
  // until the user pokes it. Colors are the production 6-section palette.
  const sections: SectionDef[] = useMemo(
    () => [
      { numeral: 'I', name: t.zineSectionProjects, href: '/active', Icon: IlluProject, half: false, accent: 'var(--accent-projects)', count: currentProjectsCount ?? null },
      { numeral: 'II', name: t.zineSectionFinished, href: '/projects', Icon: IlluFinished, half: false, accent: 'var(--accent-finished)', count: finishedCount ?? null },
      { numeral: 'III', name: t.zineSectionYarn, href: '/yarn', Icon: IlluYarn, half: false, accent: 'var(--accent-yarn)', count: yarnsCount ?? null },
      { numeral: 'IV', name: t.zineSectionTools, href: '/inventory', Icon: IlluAccessories, half: false, accent: 'var(--accent-tools)', count: inventoryCount ?? null },
      { numeral: 'V', name: t.zineSectionTechniques, href: '/techniques', Icon: IlluTechniques, half: false, accent: 'var(--accent-techniques)', count: techniquesCount ?? null },
      { numeral: 'VI', name: t.zineSectionIdeas, href: '/inspiration', Icon: IlluInspiration, half: false, accent: 'var(--accent-ideas)', count: inspirationsCount ?? null },
    ],
    [t, currentProjectsCount, finishedCount, yarnsCount, inventoryCount, techniquesCount, inspirationsCount],
  );

  return (
    <div
      className="h-full flex flex-col relative overflow-hidden"
      style={{
        background: 'var(--paper-1)',
        fontFamily: fontSerif,
        color: 'var(--ink)',
        // Extend home all the way to nav top — cards meet the nav
        // with no cream strip, no border line. Veronika 2026-05-08:
        // "Erase the two lines... by widening the cards." Math:
        //   visible_strip_below_cards = main.pb - extend - nav.height
        // We want strip = 0, so:
        //   extend = main.pb - nav.height = 80 - var(--bottom-nav-height)
        // (Build 1107 dropped the +10px we'd added previously to
        // give the strip a small cream gap.)
        height: 'calc(100% + 80px - var(--bottom-nav-height, 50px))',
        marginBottom: 'calc(var(--bottom-nav-height, 50px) - 80px)',
      }}
    >
      {showTour ? (
        <OnboardingTour onDone={dismissTour} />
      ) : installNudge ? (
        <OnboardingTour installOnly onDone={dismissInstallNudge} />
      ) : null}
      {/* Each section card flashes its own accent color on press/tap.
          --accent is set per-card via inline style in the grid below; the
          :active rule below pulls from it so the flash is local to the
          tile being pressed. */}
      <style jsx global>{`
        .b1-section {
          /* 2026-05-08 build 1134 — Veronika reverted the resting
             tint: "erase the opacity colors on the Home Screen and
             keep the original idea of, full color appears only with
             click". Cards now show the body's cream halftone-paper
             texture as their resting bg; the accent color is
             reserved for the :active state below.
             (The 35% color-mix tint here was added 2026-05-07 as a
             different direction, since rolled back.) */
          background: transparent;
          /* Resting text color (was an inline style on each Link
             until build 1136 — moved here so the :active rule
             below can actually override it). */
          color: var(--ink);
          transition: background 80ms ease, color 80ms ease;
          /* Kill iOS Safari's long-press link preview / "open in
             browser" callout. Without these, holding a card triggers
             WebKit's haptic-touch preview animation (the card
             visibly enlarges) and offers an "Open" / "Copy" menu —
             Veronika reported that as a confusing UX on her iPhone
             PWA. With these flags the only press feedback is the
             accent-color flash from the :active rule below. */
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          user-select: none;
          touch-action: manipulation;
        }
        .b1-section:active,
        .b1-section.is-last-visited,
        .b1-section.is-fresh {
          background: var(--accent);
          color: #fff;
        }
        .b1-section:active .b1-halftone,
        .b1-section.is-last-visited .b1-halftone,
        .b1-section.is-fresh .b1-halftone {
          opacity: 0;
        }
        /* 2026-05-08 build 1141 — Veronika reverted 1135's accent-
           colored icons: "make the icons on all cards black again".
           Icons are B1.ink at rest, white when the card is pressed
           OR when the card is the last-visited one (build 1141 cue:
           returning to home highlights the card you came from). */
        .b1-icon {
          color: var(--ink);
        }
        /* 2026-07-04 build 1614 — scale the card illustration down on
           narrow screens together with the label + count (Sebastian's
           feedback: on a real SE the fixed 32px icon + text overflowed the
           cells once the iOS safe-area inset shortened them). The 64px-
           viewBox SVG scales cleanly; CSS width/height override the
           size={32} attribute. Fluid from 20px at 320px up to the original
           32px at ~390px+ — no change on normal/large phones. */
        .b1-icon svg {
          width: clamp(20px, calc(20px + (100vw - 320px) * 0.1714), 32px);
          height: clamp(20px, calc(20px + (100vw - 320px) * 0.1714), 32px);
        }
        .b1-section:active .b1-icon,
        .b1-section.is-last-visited .b1-icon,
        .b1-section.is-fresh .b1-icon {
          color: #fff;
        }
      `}</style>
      {/* Paper grain — repeats halftone pattern at low opacity so the
          cream surface reads as printed, not flat. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{ ...HALFTONE_LIGHT, opacity: 0.4, mixBlendMode: 'multiply' }}
      />

      {/* Masthead — top padding includes the iOS safe-area inset so the
          masthead text doesn't hide behind the status-bar icons when
          `apple-mobile-web-app-status-bar-style: black-translucent` is
          active. The cream paper still paints all the way up to the
          screen edge thanks to the parent's `background: B1.paper`.
          The FREE chip is absolutely positioned in the top-right
          corner so the title can start as close to the safe-area as
          possible, matching the mockup where "THE" sits right under
          the iOS status bar. */}
      <div
        className="relative z-10"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top) + 2px)',
          paddingLeft: 18,
          paddingRight: 18,
          paddingBottom: 6,
          borderBottom: '2px solid var(--ink)',
        }}
      >
        {/* Was the FREE pill — now the tip-jar entry point (Veronika
            msg 5003, 2026-06-12): same ink-pill visual, opens Ko-fi. */}
        <a
          href="https://ko-fi.com/knitterslibrary"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="home-support-pill"
          style={{
            position: 'absolute',
            top: 'calc(env(safe-area-inset-top) + 4px)',
            right: 18,
            background: 'var(--ink)',
            color: 'var(--paper-1)',
            padding: '1px 6px',
            fontFamily: 'var(--font-mono)',
            fontSize: 9,
            letterSpacing: '0.06em',
            textDecoration: 'none',
          }}
        >
          {t.tipJarPill}
        </a>
        <div
          style={{
            margin: 0,
            fontFamily: 'var(--font-geist-sans), system-ui, sans-serif',
            fontWeight: 800,
            // 2026-07-04 build 1613 — iPhone SE fix. Was a fixed 56px,
            // tuned for ~390px phones. On the 320px-wide SE the masthead
            // ate so much vertical space that the 3rd tile row (TECHNIKY/
            // NÁPADY) got pushed under the bottom nav. clamp() scales the
            // headline down only on narrow screens: 46px at 320, capping
            // back to the original 56px at ~386px+ (no change on normal/
            // large phones). See the grid + label changes below — all three
            // are the same "fixed metrics too big for the SE" root cause.
            fontSize: 'clamp(44px, 14.5vw, 56px)',
            lineHeight: 0.85,
            letterSpacing: '-0.03em',
            textTransform: 'uppercase',
          }}
        >
          The
          <br />
          Knitter&rsquo;s
          <br />
          <span
            style={{
              fontStyle: 'italic',
              fontFamily: fontSerif,
              fontWeight: 300,
              textTransform: 'none',
              letterSpacing: '-0.02em',
            }}
          >
            library
          </span>
        </div>
        <div
          style={{
            borderTop: '1px solid var(--ink)',
            marginTop: 6,
            paddingTop: 4,
            fontFamily: 'var(--font-mono)',
            fontSize: 8.5,
            // 2026-05-08 build 1145 — when a self-message is set,
            // drop the letter-spacing to 0 (was 0.04em on the
            // default tagline). Tighter spacing fits ~7% more
            // characters on the same line, letting the user
            // message ceiling rise from 50 to 60 chars while still
            // fitting on the smallest iPhone width. The default
            // tagline keeps its 0.04em spacing for the "newspaper"
            // feel — only user-typed messages tighten up.
            letterSpacing: selfMessage ? 0 : '0.04em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {/* Build 1138: dropped the right-side "EST. 2026" span.
              Build 1144: when the user has set a "write yourself a
              message" in profile/you, it replaces the entire
              default tagline. Otherwise the default "{NAME}, GRAB
              YOUR NEEDLES — YOUR YARNS ARE BUZZING" stays.
              white-space: nowrap + overflow ellipsis added 1145 as
              a defensive guard — if a future device renders mono
              wider than expected and the self-message overflows,
              ellipsis crops it cleanly instead of wrapping to two
              lines and breaking the masthead height. */}
          {selfMessage
            ? selfMessage.toUpperCase()
            : profileName
              ? `${profileName.toUpperCase()}, ${t.zinePrintedFor}`
              : t.zinePrintedFor}
        </div>
      </div>

      {/* Sheep strip — replaces the previous FROM THE DESK / "X on
          the needles, Y in the bin." text band per Veronika's
          2026-05-08 ask. Pixel-art sheep walks slowly across the
          full width, scattered ink dots on the meadow floor.
          Inline SVG + CSS keyframes, no JS frame loop, no GIF. */}
      <div
        className="relative z-10"
        style={{
          height: 60,
          marginTop: 8,
          // 2026-05-08 Veronika "These lines should be bolder" —
          // matches the masthead divider's 2px weight (line 211).
          // Also matches the nav's borderTop (restored same build).
          // The card section is now framed top + bottom by two
          // equally-bold horizontal lines.
          borderBottom: '2px solid var(--ink)',
          overflow: 'hidden',
        }}
      >
        <SheepStrip />
      </div>

      {/* Index grid */}
      <div
        className="flex-1 grid grid-cols-2 relative z-10"
        style={{
          // No horizontal padding (was '10px 14px') so cards extend
          // edge-to-edge of the max-w-lg container — matches the
          // bottom nav cells which also span the full max-w-lg
          // width. Pre-build-1091, the grid had 14px horizontal
          // padding which inset the cards 14px on each side, while
          // the nav cells extended to within 9px of the screen edge.
          // The 14px asymmetry made cards visibly narrower than the
          // nav — Veronika flagged "card width is wrong, center is
          // off" on 2026-05-08.
          // No padding, no border. Cards extend edge-to-edge AND
          // top-to-bottom of the grid container with no surrounding
          // gap. Veronika 2026-05-08: "Erase the two lines (marked
          // by red dots) by widening the cards" — those lines were
          // the grid's borderTop (gap with sheep strip above) and
          // the nav's borderTop (gap with nav below). Removing
          // both makes the colored cards extend right up to the
          // sheep meadow on top and right down to the nav cells
          // on the bottom.
          // 2026-05-09 build 1155 — Veronika revised the
          // equalized 1154 margins: "we will have to make the
          // margin bigger so it is alligned with the line
          // underlining the text 'library'. So make the margins
          // wider but from up and side the same size". So all
          // four sides go to 18px — matches the masthead's
          // paddingLeft/Right (where the "library" underline
          // sits) so the cards' outer edges line up with the
          // masthead's content column on left, right, AND top.
          // Cards re-tighten vertically; sheep strips remain
          // edge-to-edge per her earlier note.
          paddingLeft: 18,
          paddingRight: 18,
          paddingTop: 18,
          paddingBottom: 18,
          gap: 0,
          marginTop: 0,
          // 2026-07-04 build 1613 — iPhone SE "not centered" fix.
          // `'1fr 1fr'` is really `minmax(auto, 1fr)` twice, so each
          // column's MINIMUM is its min-content — the widest label. On the
          // 320px SE, "DOKONČENÉ" (~141px at the old 22px/800) is wider than
          // an equal split, so Grid grew the RIGHT column (161px) and shrank
          // the left (139px), then let the right column eat the grid's right
          // padding — the whole grid slid right (18px margin on the left, ~2px
          // on the right). That lopsidedness is exactly the "not centered"
          // Veronika saw. `minmax(0, 1fr)` pins the min at 0 so both columns
          // are truly equal and stay centered; the label clamp() below keeps
          // the long words from overflowing the now-fixed cell. No visual
          // change on wider phones (columns were already equal there).
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          // Let the grid shrink below its rows' content height on short
          // screens. As a flex-1 item its default `min-height: auto` refused
          // to go below 3×110px of cell min-height, forcing vertical overflow
          // that clipped row 3 behind the nav. With `minHeight: 0` (+ the
          // cells' own `minHeight: 0` below) the `1fr 1fr 1fr` rows compress
          // to fit the available height, so all six tiles + counts land above
          // the nav on the SE. Tall phones are unaffected (plenty of room).
          minHeight: 0,
          /* Force all 3 rows to share the available grid height
             evenly. Without this the auto-sized rows let cells
             grow to content-height — the bottom row (TECHNIQUES /
             IDEAS) ended up visibly shorter than the first two
             since the flex-1 container had extra vertical space
             that wasn't distributed to the rows. `1fr 1fr 1fr`
             gives all 6 cells exactly the same dimensions. */
          gridTemplateRows: '1fr 1fr 1fr',
        }}
      >
        {sections.map((s, i) => {
          return (
            <Link
              key={s.numeral}
              href={s.href}
              onClick={() => {
                if (typeof window === 'undefined') return;
                sessionStorage.setItem('knitlib:lastCardHref', s.href);
              }}
              className={`b1-section relative flex flex-col justify-between${
                freshLaunch ? ' is-fresh' : lastVisitedHref === s.href ? ' is-last-visited' : ''
              }`}
              style={{
                // 2026-07-04 build 1614 — fluid vertical padding (7px at
                // 320px → the original 10px at ~390px+) so the icon + label +
                // count fit the shorter SE cells. Horizontal padding stays 10px.
                padding: 'clamp(7px, calc(7px + (100vw - 320px) * 0.0429), 10px) 10px',
                // 2026-05-08 build 1150: borderRight removed from
                // col 0 cells. The central vertical divider is now
                // drawn by an absolutely-positioned `<div>` below
                // (in the grid's own scope) so we can give it a
                // top + bottom margin per Veronika's red-dot ask:
                // "the vertical line dividing the cards has a
                // little margin up and down". Horizontal inner
                // dividers (between rows) stay as borderBottom on
                // cells — those weren't called out.
                borderBottom: i < 4 ? '1px solid var(--ink)' : 'none',
                // 2026-05-08 build 1136: removed `color: B1.ink`
                // inline style. It was higher-specificity than
                // `.b1-section:active { color: #fff }`, so the
                // title + count text stayed black on press even
                // though the background flipped to the accent
                // color. Veronika: "when I click on the card, the
                // full color appears and texts and icons will turn
                // white". Resting color now lives in the CSS rule
                // .b1-section { color: B1.ink } (added below) so
                // the :active rule can override it.
                // 2026-07-04 build 1613 — was 110px. A hard floor here is
                // what made the flex-1 grid refuse to shrink on the iPhone SE
                // (see the grid's `minHeight: 0` note above), clipping row 3
                // under the nav. `0` lets the `1fr 1fr 1fr` rows size to the
                // available height; on normal/tall phones each row is already
                // far taller than 110px so nothing changes there.
                minHeight: 0,
                textDecoration: 'none',
                ['--accent' as string]: s.accent,
              } as React.CSSProperties}
            >
              {s.half && (
                <div
                  aria-hidden
                  className="b1-halftone absolute inset-0 pointer-events-none"
                  style={{ ...HALFTONE, opacity: 0.18 }}
                />
              )}
              {/* Top: illustration alone at top-right.
                  Bottom: title + count stacked together at bottom-
                  left, almost touching (matches Veronika's spec —
                  she wants both text elements clustered in the
                  bottom-left corner with very tight spacing between
                  them). */}
              <div className="relative flex justify-end items-start">
                <div className="b1-icon" style={{ flexShrink: 0 }}>
                  <s.Icon size={32} w={1.7} />
                </div>
              </div>
              <div className="relative">
                <div
                  style={{
                    fontFamily: 'var(--font-geist-sans), system-ui, sans-serif',
                    fontWeight: 800,
                    // 2026-07-04 build 1614 — fluid label. On a real SE the
                    // iOS safe-area inset shortens the cells enough that the
                    // label + count overflowed the bottom (Sebastian's report);
                    // build 1613's clamp(18,5.6vw,22) didn't shrink far enough.
                    // 16px at 320px → the original 22px at ~390px+. Paired with
                    // the fluid count/icon/padding so the whole card scales
                    // together on the narrow size class.
                    fontSize: 'clamp(16px, calc(16px + (100vw - 320px) * 0.0857), 22px)',
                    letterSpacing: '-0.005em',
                    textTransform: 'uppercase',
                    lineHeight: 1,
                  }}
                >
                  {s.name}
                </div>
                <div
                  className="flex items-baseline"
                  style={{ gap: 5, marginTop: 2 }}
                >
                  <div
                    style={{
                      fontFamily: 'var(--font-geist-sans), system-ui, sans-serif',
                      fontWeight: 700,
                      // 2026-07-04 build 1614 — fluid count numeral, matches
                      // the label: 14px at 320px → the original 18px at ~390px+.
                      fontSize: 'clamp(14px, calc(14px + (100vw - 320px) * 0.0571), 18px)',
                      lineHeight: 1,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {s.count == null
                      ? '—'
                      : String(s.count).padStart(2, '0')}
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontWeight: 500,
                      fontSize: 8.5,
                      letterSpacing: '0.14em',
                      opacity: 0.7,
                    }}
                  >
                    {t.zineEntries}
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
        {/* Central vertical card divider with top + bottom margin.
            Veronika 2026-05-08 (red-dot photo): the line between
            PROJECTS|FINISHED → YARNS|TOOLS → TECHNIQUES|IDEAS
            should NOT touch the top of the cards section nor the
            bottom — small visual breathing room at each end.
            Implemented as an absolutely-positioned 1px-wide div
            inside the grid (which has `position: relative`); the
            cells themselves no longer carry borderRight on col 0. */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            // 2026-05-09 build 1156 — Veronika: "The vertical line
            // dividing the cards in the middle should also have the
            // same margins up and down". Bumps the divider's top
            // and bottom from 8px (build 1150) to 18px so it
            // matches the cards-grid's new outer 18px inset on all
            // sides (build 1155). Now the divider's endpoints sit
            // exactly at the same vertical level as the cards'
            // colored fill on top and bottom.
            top: 18,
            bottom: 18,
            left: 'calc(50% - 0.5px)',
            width: 1,
            background: 'var(--ink)',
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* Thin sheep strip — between cards and bottom nav. Veronika
          2026-05-08: "add one more animation line which will be
          thinner. It will be placed between bottom line and cards.
          The cards will be shrank to bit less high". Single-plane
          variant of the meadow above; the cards above this (which
          fill flex-1) will naturally shrink by this strip's height. */}
      <div
        className="relative z-10"
        style={{
          height: 26,
          // borderTop separates the strip from the cards above.
          // No borderBottom — the nav's borderTop (2px) provides
          // the bottom line of this strip's frame.
          borderTop: '2px solid var(--ink)',
          overflow: 'hidden',
        }}
      >
        <ThinSheepStrip />
      </div>

    </div>
  );
}

